import { FormattedMessage } from 'react-intl';

import { usePopularDocuments, useRelatedDocuments } from '../hooks/useDocuments';

import DocumentTypeIcon from '../components/icons/DocumentTypeIcon';
import RowTitle from '../components/partials/RowTitle';

export function createDocumentsRow(query, blockProps = null): DocumentsRowBlock {
    return {
        type: 'documents-row',
        query,
        // presentation: 'grid',
        cardWidth: 200,
        cardSize: 'small',
        cardTheme: 'box',
        count: 6,
        placeholderCount: 6,
        summaryPosition: 'inline',
        background: 'rgba(255, 255, 255, 0.5)',
        withBorderRadius: true,
        ...blockProps,
    };
}

export function createRelatedDocumentsRow(id, query, blockProps = null): DocumentsRowBlock {
    return {
        type: 'documents-row',
        hook: 'related',
        paginated: false,
        query: {
            id,
            ...query,
        },
        // presentation: 'grid',
        cardWidth: 200,
        cardSize: 'small',
        cardTheme: 'box',
        count: 6,
        placeholderCount: 6,
        summaryPosition: 'inline',
        background: 'rgba(255, 255, 255, 0.5)',
        withBorderRadius: true,
        ...blockProps,
    };
}

export function createAuthorRow(slug, blockProps = null) {
    return createDocumentsRow(null, {
        type: 'author-row',
        slug,
        ...blockProps,
    });
}

export function createCollectionRow(slug, blockProps = null) {
    return {
        type: 'collection-row',
        slug,
        // presentation: 'grid',
        cardWidth: 200,
        count: 6,
        placeholderCount: 6,
        withBorderRadius: true,
        // cardTheme: 'box',
        cardTheme: 'rounded',
        // cardType: 'over',
        // cardTheme: 'outline',
        cardSize: 'small',
        ...blockProps,
    };
}

export function createSerieRow(slug, blockProps = null, query = null) {
    return {
        type: 'serie-row',
        slug,
        presentation: 'grid',
        cardWithout: ['author', 'time', 'serie', 'collection', 'type-icon'],
        cardWith: ['episode'],
        query: {
            order: 'new',
            ...query,
        },
        maxColumns: 'auto',
        ...blockProps,
    };
}

export function createMicromagRow(blockProps = null) {
    const { query = null, ...props } = blockProps || {};
    return {
        type: 'summary-row',
        summary: {
            title: 'Micromag URBANIA',
            description:
                'Découvrez notre nouveau format de magazine gratuit en format numérique. Vous pouvez y consulter du contenu exclusif, nos suggestions de la semaine et même votre horoscope.',
            icon: <DocumentTypeIcon type="micromag" />,
            buttonLabel: <FormattedMessage defaultMessage="S’abonner" description="Button label" />,
            link: '/micromags',
            withButton: true,
        },
        // summaryPosition: 'bottom',
        presentation: 'row',
        itemsType: 'documents',
        cardType: 'over',
        cardSize: 'big',
        cardTheme: 'outline',
        cardRatio: 'micromag',
        cardWidth: 250,
        cardWithout: 'icon',
        loading: 'lazy',
        background: 'rgba(255, 255, 255, 0.5)',
        withBorderRadius: true,
        count: 12,
        placeholderCount: 12,
        query: {
            type: 'micromag',
            ...query,
        },
        ...props,
    };
}

export function createVideosRow(blockProps = null): DocumentsRowBlock {
    const { query = null, ...props } = blockProps || {};
    return {
        role: 'block',
        type: 'documents-row',
        presentation: 'row',
        query: {
            type: 'video',
            ...query,
        },
        loading: 'lazy',
        cardType: 'over',
        cardSize: 'big',
        cardTheme: 'outline',
        cardRatio: 'vertical',
        cardWidth: 300,
        ...props,
    };
}

export function createLayoutRow(layout, blockProps = null): DocumentsRowBlock {
    return {
        type: 'documents-row',
        presentation: 'layout',
        layout,
        withSeparator: true,
        count: layout.split('_').reduce((total, count) => total + parseInt(count, 10), 0),
        ...blockProps,
    };
}

export function createDocumentsGrid(count, blockProps = null): ItemsBlock {
    return {
        type: 'items',
        itemsType: 'documents',
        presentation: 'grid',
        count,
        ...blockProps,
    };
}

export function createRegionsList(query = null, blockProps = null): ItemsBlock {
    return {
        type: 'items',
        itemsType: 'regions',
        query,
        presentation: 'grid',
        ...blockProps,
    };
}

export function createLocationsList(items, query = null, blockProps = null): ItemsBlock {
    return {
        type: 'items',
        itemsType: 'locations',
        items,
        withoutLoader: items !== null,
        query,
        presentation: 'grid',
        columns: 1,
        cardType: 'horizontal',
        cardTheme: 'box-translucent-dark',
        withoutMaxWidth: true,
        ...blockProps,
    };
}

export function createLocationTypesList(count = 10, query = null, blockProps = null): ItemsBlock {
    return {
        type: 'items',
        itemsType: 'location_types',
        query: {
            for: 'locations',
            order: 'count',
            ...query,
        },
        count,
        cardSize: 'small',
        cardTheme: 'pill',
        presentation: 'inline',
        ...blockProps,
    };
}

export function createDocumentsMapList(query = null, blockProps = null): ItemsBlock {
    return {
        type: 'items',
        itemsType: 'documents',
        query,
        presentation: 'grid',
        columns: 1,
        cardType: 'horizontal',
        cardTheme: 'box-translucent-dark',
        cardRatio: 'square',
        cardWithout: ['duration', 'time'],
        cardWith: ['locations', 'brand-icon'],
        ...blockProps,
    };
}

export function createHomeSectionGrid({ cardWithout = [], ...props }): DocumentsRowBlock {
    return {
        role: 'block',
        type: 'documents-row',
        count: 12,
        loading: 'lazy',
        cardType: 'vertical',
        cardWithout,
        cardWidth: 200,
        ...props,
    };
}

interface BlockContext {
    urlGenerator?: UrlGenerator;
    query?: { [key: string]: unknown };
}

interface RowOptions extends BlockContext {
    url?: string;
    [key: string]: unknown;
}

export function createBrandSeriesRow(
    brand: Brand,
    { url, urlGenerator, query = null, ...props }: RowOptions,
): CollectionsRowBlock {
    const { handle: brandHandle, slug: brandSlug } = brand;
    return {
        type: 'collections-row',
        presentation: 'grid',
        title: (
            <RowTitle
                url={
                    url ||
                    urlGenerator('videos', {
                        brand: brandSlug,
                    })
                }
            >
                <FormattedMessage defaultMessage="Séries vidéos" description="Section title" />
            </RowTitle>
        ),
        count: 6,
        query: {
            brand: brand.id,
            placement: [`videos_collections_${brandHandle}`, `podcasts_collections_${brandHandle}`],
            type: ['serie', 'serie_podcast'],
            ...query,
        },
        loading: 'lazy',
        cardImageLoading: 'lazy',
        cardType: 'vertical',
        cardRatio: 'vertical',
        cardTheme: 'rounded',
        cardWidth: 200,
        ...props,
    };
}

export function createBrandPopularRow(
    brand: Brand,
    { url, urlGenerator, query = null, ...props }: RowOptions,
): DocumentsRowBlock {
    const { id: brandId, handle: brandHandle, slug: brandSlug } = brand;
    return createLayoutRow('1_1_1_4', {
        cardTheme: 'box-translucent',
        title: (
            <RowTitle url={url || urlGenerator('popular', { brand: brandSlug })}>
                <FormattedMessage
                    defaultMessage="Les plus populaires"
                    description="Section title"
                />
            </RowTitle>
        ),
        query: {
            brand: brandId,
            // placement: [`home_popular_${brandHandle}`, `home_featured_${brandHandle}`],
            ...query,
        },
        clickRef: 'home_popular',
        hook: 'popular',
        loading: 'lazy',
        cardImageLoading: 'lazy',
        uniqueDocuments: true,
        withBorder: false,
        ...props,
    });
}

export function createBrandNewRow(
    brand: Brand,
    { url, urlGenerator, query = null, ...props }: RowOptions,
): DocumentsRowBlock {
    const { id: brandId, handle: brandHandle, slug: brandSlug } = brand;
    return createLayoutRow('1_1_1_4', {
        cardTheme: 'box-translucent',
        title: (
            <RowTitle url={url || urlGenerator('all', { brand: brandSlug })}>
                <FormattedMessage defaultMessage="Les plus récents" description="Section title" />
            </RowTitle>
        ),
        query: {
            brand: brandId,
            exclude_type: ['newsletter'],
            placement: [`home_featured_${brandHandle}`],
            ...query,
        },
        clickRef: 'home_new',
        loading: 'lazy',
        cardImageLoading: 'lazy',
        uniqueDocuments: true,
        withBorder: false,
        ...props,
    });
}

export function createBrandVideosRow(
    brand: Brand,
    { url, urlGenerator, query = null, ...props }: RowOptions,
): DocumentsRowBlock {
    const { id: brandId, handle: brandHandle, slug: brandSlug } = brand;
    return createVideosRow({
        title: (
            <RowTitle url={url || urlGenerator('videos', { brand: brandSlug })}>
                <FormattedMessage defaultMessage="Vidéos récentes" description="Section title" />
            </RowTitle>
        ),
        query: {
            brand: brandId,
            placement: `videos_featured_${brandHandle}`,
            ...query,
        },
        clickRef: 'home_videos',
        loading: 'lazy',
        cardImageLoading: 'lazy',
        uniqueDocuments: true,
        withBorder: false,
        ...props,
    });
}
